import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import { classNames } from "@/utils/classNames";

import { ReactComponent as Tick } from "@assets/icons/inbox/tick.svg";

import { ReactComponent as ChevronUp } from "@assets/icons/inbox/chevron-up.svg";

import { ReactComponent as ChevronDown } from "@assets/icons/inbox/chevron-down.svg";

import { useViewportClampedPanel } from "@/utils/useViewportClampedPanel";

import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

import { ButtonSize } from "../Button/types";
import { SvgIcon } from "../Icon/SvgIcon";
import { Checkbox } from "../Checkbox/Checkbox";
import { Information } from "../Information/Information";

/**
 * The panel scrolls after seven rows. Every lead-priority filter lists seven buckets since "?"
 * became one (PREC-4075), and at six rows the panel ended exactly on a row boundary, so the
 * seventh read as absent rather than as below the fold.
 */
const PANEL_SCROLL_CLASSES = "max-h-[260px] overflow-y-auto bb-scrollbar-darker";

export interface DropdownWithCheckboxesItem {
	id: number | string | null;
	title: string;
	icon?: JSX.Element;
	count?: number;
	information?: string;
}

export interface DropdownWithCheckboxesState {
	id: number | string | null;
	title: string;
	icon?: JSX.Element;
	count?: number;
	checked: boolean;
	information?: string;
}

export interface DropdownWithCheckboxesProps {
	handleSelect: (value: DropdownWithCheckboxesItem[]) => void;
	data: DropdownWithCheckboxesItem[];
	label?: string;
	title?: string;
	testId?: string;
	floating?: boolean;
	value?: DropdownWithCheckboxesItem[];
	defaultValue?: DropdownWithCheckboxesItem[];
	disabled?: boolean;
	size?: ButtonSize;
	classNameDropdown?: string;
	classNameButton?: string;
	showTickIcon?: boolean;
	closeByClick?: boolean;
	error?: boolean;
	dropdownAlign?: "left" | "right";
	variant?: "default" | "pill";
	icon?: JSX.Element;
	portal?: boolean;
	/** Allow unchecking the last item (by default at least one selection is enforced). */
	allowEmpty?: boolean;
}

export const DropdownWithCheckboxes: FunctionComponent<DropdownWithCheckboxesProps> = ({
	handleSelect,
	data,
	label,
	title,
	testId,
	floating,
	defaultValue,
	disabled,
	size,
	error,
	classNameDropdown,
	classNameButton,
	showTickIcon,
	value,
	dropdownAlign = "left",
	variant = "default",
	icon,
	portal,
	allowEmpty,
}) => {
	const { t } = useTranslation();
	const [selectedValue, setSelectedValue] = useState<DropdownWithCheckboxesState[] | undefined>(
		data?.map((item) => ({
			...item,
			checked: defaultValue?.map((d) => d.id)?.includes(item.id) || false,
		})) as DropdownWithCheckboxesState[]
	);

	useEffect(() => {
		if (value) {
			const newValues = data.map((item) => ({
				...item,
				checked: value.map((v) => v.id)?.includes(item.id) || false,
			}));

			setSelectedValue(newValues);
		}
	}, [value]);

	const selectedValues = useMemo(() => {
		if (selectedValue) {
			return selectedValue.filter((item) => item.checked);
		}

		return [];
	}, [selectedValue]);
	// Dropdown Logic
	const [showDropdown, setShowDropdown] = useState(false);
	const selectFieldRef = useRef<HTMLDivElement>(null);
	const dropdownMenuRef = useRef<HTMLDivElement>(null);

	// Keeps the portalled panel inside the viewport wherever the trigger sits (PREC-4138).
	useViewportClampedPanel(dropdownMenuRef, showDropdown);
	const [portalPosition, setPortalPosition] = useState<{
		top: number;
		left: number;
		width: number;
	} | null>(null);

	const buttonSize = useMemo(() => {
		if (size === ButtonSize.XS) {
			return "py-1 !h-[32px] !rounded-[10px] text-xs font-semibold";
		}

		if (size === ButtonSize.S) {
			return "py-2 !h-[38px]";
		}

		return "py-[10px]";
	}, [size]);

	useEffect(() => {
		const handleClick = (event: MouseEvent) => {
			const target = getComposedEventTarget(event);

			const insideTrigger =
				selectFieldRef.current && selectFieldRef.current.contains(target as Node);
			const insidePortalMenu =
				dropdownMenuRef.current && dropdownMenuRef.current.contains(target as Node);

			if (!insideTrigger && !insidePortalMenu) {
				if (showDropdown) {
					setShowDropdown(false);
				}
			}
		};

		window.addEventListener("click", handleClick);

		return () => window.removeEventListener("click", handleClick);
	}, [selectFieldRef, showDropdown]);

	useEffect(() => {
		if (!portal || !showDropdown) {
			return;
		}

		const updatePosition = () => {
			if (selectFieldRef.current) {
				const rect = selectFieldRef.current.getBoundingClientRect();

				setPortalPosition({
					top: rect.bottom,
					left: rect.left,
					width: rect.width,
				});
			}
		};

		updatePosition();

		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);

		return () => {
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
		};
	}, [portal, showDropdown]);

	// Result rendering
	const renderResults = () => {
		const results = data;

		if (!data.length) {
			return <p className="py-2.5 px-3 text-sm italic text-gray-700">{t("basics.noResults")}</p>;
		}

		return (
			<div className={results.length ? "border-b border-b-border items-start" : ""}>
				{selectedValue?.map((item) => {
					const { id, title, count, information } = item;

					return (
						<div
							key={`option-select-${id}`}
							className={classNames(
								"flex flex-row justify-between items-center text-sm text-left text-gray-900 w-full bg-white hover:bg-gray-100 duration-200 cursor-pointer relative whitespace-nowrap"
							)}
						>
							<div className="flex flex-row w-full">
								<Checkbox
									className="pl-2"
									isChecked={item.checked}
									labelClass="max-w-[calc(100%-40px)] cursor-pointer py-2.5 px-3 pl-0"
									onChange={() => {
										const updatedSelectedValue = selectedValue?.map((v) => {
											if (v.id === item.id) {
												return {
													...v,
													checked: !item.checked,
												};
											}

											return v;
										});

										const selected = updatedSelectedValue?.filter((v) => v.checked);

										if (allowEmpty || selected.length >= 1) {
											setSelectedValue(updatedSelectedValue);
											handleSelect(selected);
										}
									}}
								>
									<div className="flex flex-row items-center justify-between w-full no-wrap gap-1">
										<span className="whitespace-nowrap grow overflow-hidden text-ellipsis">
											{title}
										</span>
										<span className="flex-shrink-0">
											{information && <Information information={information} />}
										</span>
										<span className="text-brand-light">{count ? count : ""}</span>
									</div>
								</Checkbox>
							</div>

							{showTickIcon && item.id === selectedValue?.[0]?.id && (
								<SvgIcon
									className="w-[20px] h-[20px] text-brand-light pointer-events-none"
									svgIcon={Tick}
								/>
							)}
						</div>
					);
				})}
			</div>
		);
	};

	const handleClickInput = () => setShowDropdown(!showDropdown);

	return (
		<div
			ref={selectFieldRef}
			className={classNames("relative ", variant === "pill" ? "w-fit" : "w-full")}
		>
			{label && (
				<label className="block mb-2 text-sm font-medium text-gray-900 appearance-none">
					{label}
				</label>
			)}

			<button
				className={classNames(
					"relative",
					variant === "pill" ? "w-fit" : "w-full",
					disabled && "opacity-50"
				)}
				data-test={testId}
				onClick={!disabled ? handleClickInput : undefined}
			>
				{variant === "pill" ? (
					<div
						className={classNames(
							"inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-lg border border-border-light bg-brand-white px-2 py-[5px] text-xs font-medium text-text-meta hover:bg-background-light duration-200",
							classNameButton,
							error ? "border !border-status-error" : ""
						)}
						data-test="search-input"
					>
						{icon && <span className="flex items-center text-gray-700">{icon}</span>}
						<span className="whitespace-nowrap max-w-[220px] overflow-hidden text-ellipsis">
							{title ? `${t(`${title}`)}: ` : ""}
							{selectedValues.length
								? selectedValues.map((a) => a.title).join(", ")
								: t("basics.pleaseSelect")}
						</span>
						<SvgIcon
							className="w-[14px] h-[14px] text-text-disabled pointer-events-none"
							svgIcon={showDropdown ? ChevronUp : ChevronDown}
						/>
					</div>
				) : title ? (
					<div
						className={classNames(
							"input-styles pr-12",
							"inline-flex ",
							"w-full",
							"pl-3 shadow-sm ",
							buttonSize,
							classNameButton
						)}
						data-test="search-input"
					>
						<div className="inline-flex w-full items-center min-w-0">
							<div className="flex-shrink-0">{t(`${title}`)}</div>
							<div className="flex-1 min-w-0 whitespace-nowrap overflow-hidden text-ellipsis">
								{selectedValues && selectedValues.length > 1
									? `: ${selectedValues?.map((a) => a.title).join(", ")}`
									: selectedValues.length === 1
										? `: ${selectedValues?.[0].title}`
										: ""}
							</div>{" "}
						</div>
						<div
							className={classNames(
								"absolute right-[10px]  translate-y-[-50%] peer-focus:text-brand-light",
								"top-[50%]"
							)}
						>
							<SvgIcon
								className="w-[20px] h-[20px] pointer-events-none"
								svgIcon={showDropdown ? ChevronUp : ChevronDown}
							/>
						</div>
					</div>
				) : (
					<div
						className={classNames(
							"input-styles flex flex-row pr-8 peer",
							"pl-3 shadow-sm ",
							buttonSize,
							classNameButton,
							error ? "border !border-status-error" : ""
						)}
						data-test="search-input"
					>
						<div className="flex-shrink-0 whitespace-nowrap max-w-[calc(100%)] overflow-hidden text-ellipsis">
							{selectedValues.length > 1
								? `${selectedValues.map((a) => a.title).join(", ")}`
								: (selectedValues?.[0]?.title && `${selectedValues?.[0]?.title}`) ||
									t("basics.pleaseSelect")}
						</div>
						<div
							className={classNames(
								"absolute right-[10px]  translate-y-[-50%] transform text-gray-500 peer-focus:text-brand-light",
								"top-[50%]"
							)}
							onClick={() => setShowDropdown(!showDropdown)}
						>
							<SvgIcon
								className="w-[20px] h-[20px] pointer-events-none"
								svgIcon={showDropdown ? ChevronUp : ChevronDown}
							/>
						</div>
					</div>
				)}
			</button>

			{showDropdown &&
				(portal ? (
					ReactDOM.createPortal(
						<div
							ref={dropdownMenuRef}
							className={classNames(
								classNameDropdown ? classNameDropdown : "",
								`fixed w-max z-[2000] mt-1 bg-white border border-gray-200 rounded-md ${PANEL_SCROLL_CLASSES}`
							)}
							style={
								portalPosition
									? {
											top: portalPosition.top,
											left: portalPosition.left,
											minWidth: portalPosition.width,
										}
									: { visibility: "hidden" }
							}
							onClick={(e) => e.stopPropagation()}
						>
							{renderResults()}
						</div>,
						document.body
					)
				) : (
					<div
						className={classNames(
							classNameDropdown ? classNameDropdown : "",
							`min-w-full w-max z-[2000] mt-1 bg-white border border-gray-200 rounded-md ${PANEL_SCROLL_CLASSES}`,
							floating ? "absolute" : "relative",
							dropdownAlign === "right" ? "right-0" : "left-0"
						)}
					>
						{renderResults()}
					</div>
				))}
		</div>
	);
};
